// https://github.com/eliseumds/react-pagination-hook/blob/master/src/react-pagination-hook.ts
import { useCallback, useState } from 'react'
import { AugmentedRequired } from 'utils/type'

export type ConfigArgs = {
  numberOfPages?: number
  activePage: number
  maxButtons?: number
}

type Config = AugmentedRequired<ConfigArgs, 'maxButtons' | 'numberOfPages'>

export type PaginatorPiece =
  | {
      type: 'previous' | 'next'
      pageNumber: number
      isDisabled: boolean
    }
  | {
      pageNumber: number
      type: 'page-number'
    }
  | {
      pageNumber: number
      type: 'first-page-number'
    }
  | {
      pageNumber: number
      type: 'last-page-number'
    }
  | {
      type: 'ellipsis'
    }

type ComputeVisiblePiecesFn = (config: ConfigArgs) => PaginatorPiece[]

export interface State extends ConfigArgs {
  activePage: number
  isFirst: boolean
  isLast: boolean
  hasPrevious: boolean
  hasNext: boolean
  visiblePieces: PaginatorPiece[]
  goToPage: (pageNumber: number) => void
  setNumberOfPages: (numberOfPages: number) => void
}

// x = disabled, o = enable
// (1 x) < 1 2 3 4 5 > (6 o)
// (2 o) < 6 7 8 9 10 > 1(1 o)
// (3 o) < 11 12 13 14 15 > 1(6 o)
// (4 o) < 16 17 18 19 20 > 2(0 o)
// (5 o) < 21 22 23 > 2(3 x)
export function defaultComputeVisiblePieces(config: Config): PaginatorPiece[] {
  const { numberOfPages, maxButtons, activePage } = config
  const visiblePieces: PaginatorPiece[] = []

  if (!numberOfPages) {
    return []
  }

  const startPage = maxButtons * (Math.ceil(activePage / maxButtons) - 1) + 1

  const prePageNumber = Math.max(1, startPage - maxButtons)
  visiblePieces.push({
    type: 'previous',
    pageNumber: prePageNumber,
    isDisabled: startPage - maxButtons < 1,
  })

  for (let i = 0; i < maxButtons && i + startPage <= numberOfPages; i++) {
    visiblePieces.push({ type: 'page-number', pageNumber: i + startPage })
  }

  visiblePieces.push({
    type: 'next',
    pageNumber: Math.min(numberOfPages, startPage + maxButtons),
    isDisabled: startPage + maxButtons > numberOfPages,
  })

  return visiblePieces
}

type ConfigProps = ConfigArgs & {
  computeVisiblePieces?: ComputeVisiblePiecesFn
  setActivePage?: (page: number) => void
  scrollToTop?: boolean
}

function usePagination(config: ConfigProps) {
  let { maxButtons = 5, activePage } = config
  const { setActivePage, scrollToTop } = config
  const {
    computeVisiblePieces = defaultComputeVisiblePieces,
    numberOfPages: numberOfPagesProp = 0,
  } = config

  const [numberOfPagesState, setNumberOfPages] = useState(numberOfPagesProp)

  // prop 으로 전달 된 numberOfPages 가 있으면 사용
  const numberOfPages = numberOfPagesProp || numberOfPagesState

  if (numberOfPages && activePage > numberOfPages) {
    activePage = numberOfPages
  }

  if (numberOfPages && maxButtons > numberOfPages) {
    maxButtons = numberOfPages
  }

  const isFirst = activePage === 1
  const isLast = activePage === numberOfPages
  const hasPrevious = numberOfPages > 1 && activePage > 1
  const hasNext = activePage < numberOfPages
  const visiblePieces = computeVisiblePieces({
    maxButtons,
    activePage,
    numberOfPages,
  })
  const goToPage = useCallback<State['goToPage']>(
    (pageNumber) => {
      if (setActivePage) {
        setActivePage(pageNumber)
        if (scrollToTop && typeof window !== 'undefined') {
          window.scrollTo(0, 0)
        }
      }
    },
    [setActivePage],
  )

  const setNumberOfPagesCallback = useCallback<State['setNumberOfPages']>(
    (newNumberOfPages) => {
      setNumberOfPages(newNumberOfPages)
    },
    [],
  )

  return {
    activePage,
    isFirst,
    isLast,
    hasPrevious,
    hasNext,
    visiblePieces,
    goToPage,
    setNumberOfPages: setNumberOfPagesCallback,
    numberOfPages,
    maxButtons,
    computeVisiblePieces,
  }
}

export default usePagination
